import {useQuery} from '@apollo/client';
import {Pagination, Table, Tag} from 'antd';
import DBModal from 'components/modal/modal';
import {toast} from 'react-toastify';
import moment from 'moment';
import {useEffect, useMemo, useState} from 'react';
import styles from '../superAdminOverview.module.scss';
import {GET_REGION_INVESTOR_USERS, GET_REGION_USERS} from '../resolvers';
import UserLink from 'components/userLink/UserLink';

const formatRegionLabel = region =>
  (region || '—')
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, c => c.toUpperCase());

const formatRegionEnumCell = value =>
  value
    ? String(value)
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, c => c.toUpperCase())
    : '—';

const displayUserName = record => {
  const full = [record?.firstName, record?.lastName]
    .filter(Boolean)
    .join(' ')
    .trim();
  if (full) return full;
  return (record?.firstName || '').trim() || '—';
};

function unwrapRegionUsersResponse(data, field) {
  const node = data?.[field];
  if (node?.__typename === 'Error') {
    return {error: node, rows: [], pagination: null};
  }
  if (node?.__typename === 'ResponseWithRegionUsers') {
    return {
      error: null,
      rows: node.data ?? [],
      pagination: node.pagination ?? null,
    };
  }
  return {error: null, rows: [], pagination: null};
}

/**
 * @param {boolean} open
 * @param {string | null} region — RegionCategory.region enum key from distribution API
 * @param {'investors' | 'users'} initialListMode
 * @param {{ startDate: string | null, endDate: string | null }} appliedOverviewDates
 * @param {() => void} onClose
 */
const InvestorsRegionUsersModal = ({
  open,
  region,
  initialListMode = 'investors',
  appliedOverviewDates = {startDate: null, endDate: null},
  onClose,
}) => {
  const [listKind, setListKind] = useState(initialListMode);
  const [modalPage, setModalPage] = useState(1);
  const [modalLimit, setModalLimit] = useState(10);

  useEffect(() => {
    if (open && region) {
      setListKind(initialListMode);
      setModalPage(1);
      setModalLimit(10);
    }
  }, [open, region, initialListMode]);

  useEffect(() => {
    setModalPage(1);
  }, [listKind]);

  const filters = useMemo(() => {
    if (!region) return null;
    return {
      region,
      paginate: true,
      page: modalPage,
      limit: modalLimit,
      ...(appliedOverviewDates.startDate
        ? {startDate: appliedOverviewDates.startDate}
        : {}),
      ...(appliedOverviewDates.endDate
        ? {endDate: appliedOverviewDates.endDate}
        : {}),
    };
  }, [region, modalPage, modalLimit, appliedOverviewDates]);

  const skip = !open || !region || !filters;

  const {data: investorsData, loading: investorsLoading} = useQuery(
    GET_REGION_INVESTOR_USERS,
    {
      skip: skip || listKind !== 'investors',
      variables: {filters},
      errorPolicy: 'all',
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
    },
  );

  const {data: usersData, loading: usersLoading} = useQuery(GET_REGION_USERS, {
    skip: skip || listKind !== 'users',
    variables: {filters},
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
  });

  const activeData = listKind === 'investors' ? investorsData : usersData;
  const activeLoading =
    listKind === 'investors' ? investorsLoading : usersLoading;
  const {error, rows, pagination} = unwrapRegionUsersResponse(
    activeData,
    listKind === 'investors' ? 'getRegionInvestorUsers' : 'getRegionUsers',
  );

  useEffect(() => {
    if (error?.message) {
      toast.error(error.message);
    }
  }, [error]);

  const handleModalPageChange = (page, pageSize) => {
    if (pageSize !== modalLimit) {
      setModalLimit(pageSize);
      setModalPage(1);
    } else {
      setModalPage(page);
    }
  };

  const total = pagination?.total ?? 0;

  const columns = useMemo(
    () => [
      {
        title: 'User',
        key: 'user',
        width: 260,
        fixed: 'left',
        render: (_, record) => (
          <div className={styles.regionUsersIdentityCell}>
            <UserLink
              userId={record?.id}
              tab="overview"
              className={styles.regionUsersIdentityName}>
              {displayUserName(record)}
            </UserLink>
            <div className={styles.regionUsersIdentityEmail}>
              {record?.email?.trim() || '—'}
            </div>
          </div>
        ),
      },
      {
        title: 'Username',
        dataIndex: 'username',
        key: 'username',
        ellipsis: true,
        render: v => v || '—',
      },
      {
        title: 'Phone',
        key: 'phone',
        render: (_, record) => {
          const phone = `${record?.countryCode ?? ''}${
            record?.phoneNumber ?? ''
          }`.trim();
          return phone || '—';
        },
      },
      {
        title: 'Profile',
        key: 'profile',
        width: 88,
        render: (_, record) =>
          `${record?.profileCompletion?.completionPercentage ?? 0}%`,
      },
      {
        title: 'Investor',
        key: 'investor',
        width: 120,
        render: (_, record) => {
          const acc = record?.investorProfile?.isAccredited;
          if (acc === true) {
            return <Tag color="green">Accredited</Tag>;
          }
          if (acc === false) {
            return <Tag color="red">Unaccredited</Tag>;
          }
          return '—';
        },
      },
      {
        title: 'Joined',
        key: 'joined',
        width: 118,
        render: (_, record) =>
          record?.createdAt
            ? moment(record.createdAt).format('MMM D, YYYY')
            : '—',
      },
      {
        title: 'Region',
        key: 'region',
        render: (_, record) =>
          formatRegionEnumCell(record?.regionCategory?.region),
      },
      {
        title: 'Sub-region',
        key: 'subRegion',
        render: (_, record) =>
          formatRegionEnumCell(record?.regionCategory?.subRegion),
      },
    ],
    [],
  );

  const modalDateHint = useMemo(() => {
    if (!appliedOverviewDates.startDate && !appliedOverviewDates.endDate) {
      return 'Using full date range (no overview dates applied).';
    }
    const s = appliedOverviewDates.startDate
      ? moment(appliedOverviewDates.startDate).format('DD MMM YYYY')
      : '…';
    const e = appliedOverviewDates.endDate
      ? moment(appliedOverviewDates.endDate).format('DD MMM YYYY')
      : '…';
    return `Filtered by overview period: ${s} – ${e}`;
  }, [appliedOverviewDates]);

  const listLabel = listKind === 'investors' ? 'investors' : 'users';

  return (
    <DBModal
      isOpen={open}
      handleClose={onClose}
      width={1140}
      content={
        <div className={styles.regionUsersModal}>
          <div className={styles.regionUsersModalToolbar}>
            <div className={styles.regionUsersModalHeading}>
              <h2 className={styles.regionUsersModalTitle}>
                {formatRegionLabel(region)}
              </h2>
              <p className={styles.regionUsersModalSubtitle}>{modalDateHint}</p>
              <div
                className={styles.regionUsersListKindToggle}
                role="tablist"
                aria-label="List type">
                <button
                  type="button"
                  role="tab"
                  aria-selected={listKind === 'investors'}
                  className={
                    listKind === 'investors'
                      ? `${styles.regionUsersListKindBtn} ${styles.regionUsersListKindBtnActive}`
                      : styles.regionUsersListKindBtn
                  }
                  onClick={() => setListKind('investors')}>
                  Investors
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={listKind === 'users'}
                  className={
                    listKind === 'users'
                      ? `${styles.regionUsersListKindBtn} ${styles.regionUsersListKindBtnActive}`
                      : styles.regionUsersListKindBtn
                  }
                  onClick={() => setListKind('users')}>
                  Users
                </button>
              </div>
            </div>
          </div>
          <div className={styles.regionUsersModalTableWrap}>
            <Table
              className={'antd-table'}
              columns={columns}
              dataSource={rows}
              loading={activeLoading}
              pagination={false}
              rowKey={r => r.id}
              locale={{
                emptyText: activeLoading
                  ? 'Loading…'
                  : `No ${listLabel} match this region and filters.`,
              }}
            />
          </div>
          {total > 0 && (
            <Pagination
              className={styles.regionUsersPagination}
              current={modalPage}
              pageSize={modalLimit}
              total={total}
              onChange={handleModalPageChange}
              showSizeChanger
              showQuickJumper
              pageSizeOptions={['10', '20', '40']}
              showTotal={(t, range) =>
                `${range[0]}-${range[1]} of ${t} ${listLabel}`
              }
            />
          )}
        </div>
      }
    />
  );
};

export default InvestorsRegionUsersModal;
